import { useCallback, useState } from 'react'

import { RequestStatus } from 'core/domain/Request'

import { CapacityError } from '../../domain/errors/CapacityError'
import { OutOfSyncError } from '../../domain/errors/OutOfSyncError'
import { RatePerMileError } from '../../domain/errors/RatePerMileError'
import { type LaneProposal } from '../../domain/LaneProposal'
import {
  submitLaneProposal as submitLane,
  type SubmitLaneProposalParams,
} from '../../use-cases/submitLaneProposal'
import { useRfpContext } from '../contexts/RfpContext'

export function useLaneProposal(initialLaneProposal: LaneProposal | undefined) {
  const {
    state: {
      laneErrors: { hasCapacityError, hasRatePerMileError, hasOutOfSyncError },
      submitLaneProposalStatus,
    },
    updateLaneProposalIds,
    setLaneErrors,
    setLastSaved,
    setSubmitLaneProposalStatus,
  } = useRfpContext()

  const [savedLaneProposal, setSavedLaneProposal] = useState<LaneProposal | undefined>(
    initialLaneProposal
  )

  const submitLaneProposal = useCallback(
    async (
      submitParams: SubmitLaneProposalParams,
      { controller }: { controller: AbortController }
    ) => {
      setSubmitLaneProposalStatus(RequestStatus.Pending)
      setLaneErrors({
        hasCapacityError: false,
        hasRatePerMileError: false,
        hasOutOfSyncError: false,
      })

      try {
        const laneResult = await submitLane(submitParams, controller)

        const laneProposalId = laneResult?.id ?? submitParams.currentLaneProposal?.id

        setSavedLaneProposal(laneResult)

        setSubmitLaneProposalStatus(RequestStatus.Done)
        updateLaneProposalIds(laneProposalId, !laneResult)
        setLastSaved()
      } catch (error) {
        if (controller.signal.aborted) return

        setSubmitLaneProposalStatus(RequestStatus.Error)

        setLaneErrors({
          hasCapacityError: error instanceof CapacityError,
          hasRatePerMileError: error instanceof RatePerMileError,
          hasOutOfSyncError: error instanceof OutOfSyncError,
        })
      }
    },

    [updateLaneProposalIds, setLaneErrors, setLastSaved, setSubmitLaneProposalStatus]
  )

  const isLoading = submitLaneProposalStatus === RequestStatus.Pending
  const hasError = submitLaneProposalStatus === RequestStatus.Error

  return {
    hasCapacityError,
    hasRatePerMileError,
    hasOutOfSyncError,
    hasError,
    isLoading,
    savedLaneProposal,
    submitLaneProposal,
  }
}
